import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Switch } from "@/components/ui/switch";
import { Mic } from "lucide-react";
import { toast } from "sonner";
import { PersonaSelector, type PersonaKey } from "@/components/jarvis/PersonaSelector";

interface Props {
  groupId: string;
}

export function JarvisWhatsAppToggle({ groupId }: Props) {
  const [enabled, setEnabled] = useState(false);
  const [personaKey, setPersonaKey] = useState<PersonaKey>("syra");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingPersona, setSavingPersona] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("syra_groups")
        .select("jarvis_whatsapp_enabled, jarvis_whatsapp_persona_key")
        .eq("id", groupId)
        .maybeSingle();
      if (cancelled) return;
      setEnabled(Boolean(data?.jarvis_whatsapp_enabled));
      const k = (data as { jarvis_whatsapp_persona_key?: string | null } | null)?.jarvis_whatsapp_persona_key;
      if (k === "syra" || k === "jarvis") setPersonaKey(k);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [groupId]);

  const onToggle = async (next: boolean) => {
    setSaving(true);
    const { error } = await supabase
      .from("syra_groups")
      .update({ jarvis_whatsapp_enabled: next })
      .eq("id", groupId);
    setSaving(false);
    if (error) {
      toast.error("Falha ao atualizar Jarvis WhatsApp");
      return;
    }
    setEnabled(next);
    toast.success(next ? "Jarvis WhatsApp ativado" : "Jarvis WhatsApp desativado");
  };

  const onPersonaChange = async (next: PersonaKey) => {
    const prev = personaKey;
    setPersonaKey(next);
    setSavingPersona(true);
    const { error } = await supabase
      .from("syra_groups")
      .update({ jarvis_whatsapp_persona_key: next })
      .eq("id", groupId);
    setSavingPersona(false);
    if (error) {
      setPersonaKey(prev);
      toast.error("Falha ao salvar persona");
      return;
    }
    toast.success(`Persona definida: ${next === "jarvis" ? "J.A.R.V.I.S." : "Syra"}`);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 rounded-md border border-border/50 bg-muted/20 px-2.5 py-1.5">
        <div className="flex items-center gap-1.5 text-xs">
          <Mic className="size-3.5 text-primary" />
          <span className="font-medium">Jarvis no WhatsApp</span>
        </div>
        <Switch checked={enabled} disabled={loading || saving} onCheckedChange={onToggle} />
      </div>

      {enabled && (
        <div className="space-y-1.5 rounded-md border border-border/50 bg-muted/10 px-2.5 py-2">
          <span className="block text-[11px] font-medium text-muted-foreground">
            Persona usada nas respostas do grupo
          </span>
          <PersonaSelector
            value={personaKey}
            onChange={onPersonaChange}
            disabled={loading || savingPersona}
          />
        </div>
      )}
    </div>
  );
}
